import React, { forwardRef, useCallback, useEffect, useImperativeHandle, useRef, useState } from 'react';
import styled, { css, keyframes } from 'styled-components';
import OverlayPanel from './overlayPanel';
import CommandMenu from './scene-editors/commandMenu';
import { SceneEditorAction } from './scene-editors/sceneEditorActionBar';
import { EmptyNodeIcon, EyeIcon, EyeOffIcon, MeshNodeIcon } from '../../../../widgets/icons/scene';
import { panel } from '../../../../widgets/propertiesPanel';
import { T } from '../../../../../shared/helpers';

// Un contenitore è un Empty, non una cartella: una trasformazione, un'origine e
// tre assi. Aperto o chiuso non cambia cosa è — a dirlo è la freccia della riga —
// quindi il glifo è lo stesso, come nel prototipo.
const ICONS = {
    folderClosed: <EmptyNodeIcon />,
    folderOpen: <EmptyNodeIcon />,
    file: <MeshNodeIcon />,
    eye: <EyeIcon />,
    eyeOff: <EyeOffIcon />
};

/** Sei fasi da 180ms — acceso e spento tre volte. Lo legge sia il timer sia la durata di una fase. */
const BLINK_DURATION = 1080;

const DragPreviewLabel = styled.div<{ x: number; y: number }>`
    position: fixed;
    top: ${(props) => props.y}px;
    left: ${(props) => props.x}px;
    pointer-events: none;
    background: ${(props) => props.theme.app.textColor};
    color: ${(props) => props.theme.app.backgroundColor};
    padding: 4px 8px;
    border-radius: 4px;
    font-size: 12px;
    z-index: 9999;
`;

// LA FRECCETTA DI UN RAMO, ed è esportata perché la usa anche la vista a GRIGLIA dei materiali:
// là un rientro non esiste, e aprire o chiudere un gruppo è l'unico modo che la griglia ha di
// mostrarlo come una cosa sola. Due triangoli disegnati due volte sarebbero due cose da tenere
export const TreeTwisty = styled.button<{ $isOpen: boolean }>`
    position: relative;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 12px;
    height: 20px;
    padding: 0;
    border: 0;
    background: transparent;
    color: ${(props) => props.theme.app.textColor};
    text-align: center;
    cursor: pointer;
    user-select: none;
    margin-right: 4px;

    &::before {
        content: '';
        width: 0;
        height: 0;
        border-top: 4px solid transparent;
        border-bottom: 4px solid transparent;
        border-left: 5px solid currentColor;
        transform: rotate(${(props) => (props.$isOpen ? 90 : 0)}deg);
        transition: transform 0.2s;
    }

    &:focus-visible {
        outline: 2px solid ${(props) => props.theme.app.secondary};
        outline-offset: 1px;
    }
`;

// IL FONDO DI UNA RIGA HA UNA SORGENTE SOLA, perché il lampeggio deve saper tornare esattamente
// dov'era: scritto due volte, la riga finirebbe di lampeggiare su un colore diverso da quello che
// aveva prima.
const rowBackground = (props: { $isDragover: boolean; $selected: boolean; theme: any }) =>
    props.$isDragover ? `${props.theme.app.secondary}22` : props.$selected ? `${props.theme.app.secondary}14` : 'transparent';

// IL LAMPEGGIO — «è questa». Sei fasi da 180ms: acceso e spento tre volte, che è quel tanto che
// l'occhio segue senza doverlo cercare.
//
// SPENTO È TRASPARENTE, e non il fondo che la riga aveva. Alternare fra il lavato della riga
// scelta e uno appena più carico non si vedeva: su una riga già accesa — e quando si chiede
// «dove sta?» la riga è quasi sempre quella scelta — i due toni erano la stessa cosa. Perdere e
// riprendere il fondo è il segno che si legge da un metro, ed è anche ciò che fa il prototipo.
const blinkRow = keyframes`
    0%, 49.9% { background: var(--row-blink-hot); }
    50%, 100% { background: transparent; }
`;

const TreeNodeContainer = styled.div<{
    $depth: number;
    $isDragging: boolean;
    $isDragover: boolean;
    $selected: boolean;
    $isVisible: boolean;
    $blinking?: boolean;
}>`
    cursor: pointer;
    display: flex;
    align-items: center;

    // L'ALTEZZA DELLA RIGA È DICHIARATA, non dedotta dal glifo che ci capita dentro. Dedotta,
    // valeva ventiquattro pixel — nome, icona e occhio incollati — e un albero di venti mesh
    // diventava un blocco di testo in cui le righe si contano solo rileggendole. Trentaquattro è
    // la misura con cui una riga di elenco si distingue dalla vicina senza che serva un filo a
    // dividerle, ed è anche il pavimento comodo per un bersaglio da colpire col puntatore.
    min-height: 34px;
    padding: 4px;
    // IL RIENTRO SI SCRIVE QUI E BASTA. Lo scriveva anche ChildrenContainer, che è
    // annidato: i due si sommavano a ogni livello, e a profondità quattro il nome del
    // nodo finiva oltre il bordo destro del pannello. Restavano l'occhio e il glifo, e
    // sembrava che le mesh non avessero un nome.
    // Quattordici e non venti: il pannello è largo poco più di duecento pixel, e il
    // rientro è un'informazione — non deve costare più della cosa che indenta.
    padding-left: ${(props) => props.$depth * 14 + 4}px;
    --row-blink-hot: ${(props) => props.theme.app.secondary}66;

    background: ${rowBackground};
    border-left: 2px solid ${(props) => (props.$selected ? props.theme.app.secondary : 'transparent')};
    opacity: ${(props) => (props.$isDragging ? 0.5 : props.$isVisible ? 1 : 0.4)};
    &:hover {
        background: ${(props) => props.theme.app.pageBackgroundColor};
    }

    ${(props) =>
        props.$blinking &&
        css`
            animation: ${blinkRow} 360ms steps(1, end) 3;
        `}
`;

const TreeNodeInnerContainer = styled.div`
    min-height: 100%;
    display: flex;
    flex-direction: column;
    & > :first-child {
        margin-top: 10px;
    }
`;

// Raggruppa i figli e non li rientra: il rientro lo scrive la riga, che conosce la
// propria profondità. Qui sarebbe cumulativo, perché questi contenitori si annidano.
const ChildrenContainer = styled.div`
    min-width: 0;
`;

// I GLIFI SI MISURANO QUI. Gli SVG della scena non portano `width`/`height` — se li
// portassero, SVGO toglierebbe il `viewBox` e la figura verrebbe disegnata a 1:1 dentro
// un riquadro di sedici pixel. Chi li ospita deve quindi dargli una misura: senza, in
// una riga flex l'immagine non ha larghezza intrinseca, viene compressa a zero e la
// riga sembra semplicemente priva di icona. Da qui anche `flex: none`.
const glyphBox = (size: number) => `
    flex: none;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: ${size}px;
    height: ${size}px;
    margin-right: 8px;
    user-select: none;

    svg {
        width: ${size}px;
        height: ${size}px;
        display: block;
    }
`;

// LA SPECIE COLORA IL SUO GLIFO: verde-acqua il soggetto, viola il raggruppamento. È un tratto di
// CIÒ CHE LA RIGA È, non del suo stato — per questo non cede all'arancio della selezione, che dice
// un'altra cosa («questa è scelta»), e che la riga presa porta già addosso in altri due modi.
//
// CHI LA SPECIE NON LA DICHIARA RESTA COM'ERA: le liste che non distinguono — le camere — non
// hanno una domanda a cui il colore risponda, e un colore che dice «questo è un oggetto» su una
// camera direbbe una cosa falsa.
const Icon = styled.span<{ $kind?: string }>`
    ${glyphBox(16)}
    color: ${(props) => (props.$kind === 'group' ? panel.kindGroupInk : props.$kind === 'mesh' ? panel.kindSubjectInk : 'inherit')};
    opacity: ${(props) => (props.$kind ? 1 : 0.72)};
`;
const CustomIconContainer = styled.div`
    ${glyphBox(18)}
`;
const VisibilityIcon = styled.span`
    ${glyphBox(16)}
    margin-right: 0;
    cursor: pointer;
    opacity: 0.72;

    &:hover {
        opacity: 1;
    }
`;
// CHI CONTIENE SI LEGGE PIÙ FORTE DI CHI STA DENTRO. In un albero profondo la struttura la
// disegnano i rientri, ma i rientri si contano solo se si guarda il bordo sinistro: il grassetto
// dice a colpo d'occhio dove comincia un ramo, e lo dice al centro della riga, dove si sta già
// leggendo il nome.
const NodeName = styled.span<{ $branch?: boolean }>`
    user-select: none;
    flex: 1;
    min-width: 0;
    overflow: hidden;
    font-weight: ${(props) => (props.$branch ? 600 : 400)};
    text-overflow: ellipsis;
    white-space: nowrap;
`;

// IL NOME SI CAMBIA DOVE SI LEGGE, come nella testata dell'Inspector: nessuna icona, nessuna
// finestra. Il gesto è quello di ogni gestore di file — si sceglie la riga, e un secondo clic sul
// nome lo apre — e vale sia per una riga sia per una cartella.
//
// IL CAMPO EREDITA IL CARATTERE DELLA RIGA e restituisce lo spazio della propria cornice, o il
// nome si sposterebbe di qualche pixel nel momento in cui lo si tocca: un testo che salta mentre
// lo stai per scrivere fa dubitare di aver cliccato la cosa giusta.
const NodeNameInput = styled.input`
    flex: 1;
    min-width: 0;
    margin: -2px -5px;
    padding: 1px 4px;
    border: 1px solid ${panel.accent};
    border-radius: 4px;
    background: ${panel.field};
    color: ${panel.ink};
    font: inherit;
    outline: none;
`;

// IL CODINO DELLA RIGA — un dato di contorno in coda al nome: quante mesh usano un materiale,
// di che specie è una luce. Sta DOPO il nome e prima dei comandi, ed è sempre visibile: non è
// un comando che compare al passaggio, è una cosa che si legge scorrendo.
//
// UNA PAROLA STA UN GRADINO PIÙ SOTTO DI UN NUMERO. «Hemispheric» a corpo pieno spinge il nome
// fuori dalla riga, e un nome troncato per un dato di contorno rovescia la gerarchia. Il numero
// invece è corto e va incolonnato, che è il solo modo perché una colonna di numeri si legga
// come una colonna.
/** Il dato di contorno in coda al nome di una riga: un numero, o una parola. */
export type TreeRowNote = { text: string; title?: string; word?: boolean };

const RowNote = styled.span<{ $word?: boolean }>`
    flex: none;
    margin-left: 6px;
    color: ${(props) => props.theme.app.textColor};
    opacity: 0.65;
    font-size: ${(props) => (props.$word ? '9.5px' : '10px')};
    font-variant-numeric: tabular-nums;
    white-space: nowrap;
`;

// I COMANDI DELLA RIGA STANNO IN FONDO E COMPAIONO AL PASSAGGIO. Una colonna di occhi
// accesi a sinistra di ogni nome pesa quanto i nomi e non dice niente: se tutto è visibile,
// l'informazione è zero. Qui l'occhio si mostra sulla riga che stai toccando — o su quella
// selezionata — e resta **fisso** su ciò che è nascosto, perché quello è l'unico caso in cui
// il segno porta un'informazione.
const RowTrailing = styled.span<{ $pinned: boolean }>`
    flex: none;
    display: flex;
    align-items: center;
    gap: 2px;
    margin-left: 6px;
    opacity: ${(props) => (props.$pinned ? 1 : 0)};
    transition: opacity 0.12s ease;

    ${TreeNodeContainer}:hover &,
    ${TreeNodeContainer}:focus-within & {
        opacity: 1;
    }
`;

// Interfaccia per l'handle del TreeComponent
export interface TreeHandle {
    closeOverlay: () => void;
    selectAndOpenOverlay: (nodeId: string) => void;
}

export interface TreeComponentProps {
    treeState: {
        moveSelection: any;
        nodes: any[];
        openNodes: Set<string>;
        selectedIds: Set<string>;
        draggedId: string | null;
        dropTargetId: string | null;
        overlayNodeId: string | null;
        flatVisibleList: any[];
        setDraggedId: (id: string | null) => void;
        setDropTargetId: (id: string | null) => void;
        setOverlayNodeId: (id: string | null) => void;
        findNodeById: (id: string) => any;
        findParentId: (id: string) => string | null;
        findNodeByDataID: (nodes: any[], dataID: string) => any;
        toggleSelection: (id: string, shiftKey: boolean, ctrlKey: boolean) => void;
        toggleNode: (id: string) => void;
        toggleVisibility: (id: string) => void;
        setSelectedIds: (ids: Set<string>) => void;
        canTurnIntoFolder?: (node: any) => boolean;
    };
    // LA RICERCA NON STA QUI. È della SEZIONE, non dell'albero: nella griglia dei materiali
    // l'albero non c'è e il campo deve restare dov'era, quindi vive nel `SectionPanel`. Lo stesso
    // vale per «seleziona tutto», che è un comando fra i comandi da quando la barra esiste.
    // Restavano dentro qui un campo, una casella e quattro prop che nessuno accendeva più.
    treeLabel?: string;
    /**
     * «DOVE STA?» — la riga da portare in vista e far lampeggiare.
     *
     * ARRIVA PER PROP E NON DAL MANICO, ed è la correzione di un difetto vero: il manico è UNO
     * per quattro sezioni e chi lo lega per ultimo se lo prende, quindi una chiamata imperativa
     * poteva finire sull'albero sbagliato e non fare niente — in silenzio, che è il modo peggiore
     * di non funzionare. Una prop la riceve questo albero e nessun altro.
     *
     * IL GETTONE SERVE A CHIEDERE DUE VOLTE LA STESSA RIGA: senza, la seconda domanda sarebbe lo
     * stesso valore di prima e non succederebbe niente.
     */
    blinkRequest?: { nodeId: string; token: number } | null;
    disableDragAndDrop?: boolean;
    onNodeMoved: (nodeId: string, targetParentId: string | null, index: number) => void;
    /** `nodeId` è l'id del nodo dell'albero e `nextVisible` lo stato che deve assumere. */
    onToggleNodeVisibility?: (nodeId: string, nextVisible: boolean) => void;
    showVisibilityIcon?: boolean; // New prop to control visibility icon display
    /**
     * LA LISTA È PIATTA: lasciare una riga su una riga vuol dire **metterla lì**, non infilarla
     * dentro. Le luci sono così — una luce non contiene una luce — e prima quel gesto non faceva
     * niente del tutto: il nodo dichiarava `notTurnableIntoFolder` e il drop veniva annullato in
     * silenzio, mentre nel vecchio scene editor annidava una luce dentro l'altra, che è una cosa
     * che nella scena non significa niente.
     *
     * Con questo acceso l'albero **non tocca la struttura**: dice soltanto a chi lo ospita su
     * quale riga è caduto il trascinamento, e l'ordine nuovo lo scrive lui — dove lo sa salvare.
     * Per questo qui `onNodeMoved` riceve la riga BERSAGLIO e non un genitore.
     */
    flatList?: boolean;
    /**
     * I COMANDI CHE VOGLIONO UN SOGGETTO, riga per riga. Chi monta l'albero li costruisce già
     * legati al nodo che riceve: il menu non sceglie niente aprendosi, quindi il soggetto deve
     * essere quello sotto il «…» e non la selezione, che potrebbe stare altrove.
     */
    rowActions?: (node: any) => SceneEditorAction[];
    /**
     * IL CODINO DI UNA RIGA, quando quella lista ne ha uno da dire. Restituire `null` per le
     * righe che non lo hanno — una cartella fra i materiali, un gruppo fra le luci — invece di
     * una stringa vuota, che occuperebbe comunque il suo spazio. `word` per un'etichetta, che sta
     * un gradino sotto un numero.
     */
    rowNote?: (node: any) => TreeRowNote | null | undefined;
    /**
     * RINOMINARE SUL POSTO. Chi la dichiara abilita il gesto sulle proprie righe: un secondo clic
     * sul nome di una riga già scelta lo apre in scrittura. Riceve il nodo e non il solo
     * identificativo, perché una lista può doverne distinguere le specie — un gruppo di materiali
     * non si rinomina come un materiale.
     */
    onNodeRename?: (node: any, name: string) => void;
    disableOverlay?: boolean;
    onSelectedComponentChange?: (component: React.ReactNode | null) => void;
    /**
     * CERCARE APRE I RAMI. Un albero mostra i figli solo dei nodi aperti, e va bene finché si
     * naviga: si scende dove si vuole andare. Ma con una ricerca in corso quella regola nasconde
     * proprio ciò che si sta cercando — chi filtra vede restare i soli antenati, chiusi, e da fuori
     * sembra che la ricerca non trovi niente.
     *
     * Chi ospita l'albero lo accende quando un filtro è attivo: le righe rimaste sono già poche e
     * sono tutte pertinenti, quindi tenerle chiuse non risparmia niente a nessuno.
     */
    expandAll?: boolean;
}

const TreeComponent = forwardRef<TreeHandle, TreeComponentProps>(
    (
        {
            treeState,
            treeLabel = 'Scene items',
            blinkRequest,
            disableDragAndDrop,
            onNodeMoved,
            onToggleNodeVisibility,
            showVisibilityIcon = true,
            flatList,
            rowActions,
            rowNote,
            onNodeRename,
            disableOverlay,
            onSelectedComponentChange,
            expandAll
        },
        ref
    ) => {
        const {
            nodes,
            openNodes,
            selectedIds,
            draggedId,
            dropTargetId,
            overlayNodeId,
            setDraggedId,
            setDropTargetId,
            setOverlayNodeId,
            findNodeById,
            findParentId,
            findNodeByDataID,
            toggleSelection,
            toggleNode,
            toggleVisibility,
            setSelectedIds
        } = treeState;

        const treeContainerRef = useRef<HTMLDivElement>(null);

        const [dragPreview, setDragPreview] = useState<{
            x: number;
            y: number;
            label: string;
        } | null>(null);

        // IL PANNELLO CONSEGNATO INVECCHIAVA. Quando la colonna di destra è di qualcun altro
        // (`disableOverlay`), qui si chiama `node.component()` al clic e si passa su l'ELEMENTO che
        // ne esce: da quel momento quell'elemento tiene dentro il nodo com'era in quel momento.
        // Rinominare una riga cambiava il nodo e non il pannello, e il nome nuovo si vedeva solo
        // chiudendo e riaprendo. Da qui il pannello si rifà ogni volta che l'albero cambia — e se
        // ne va quando la riga che descriveva non c'è più.
        const publishComponent = useRef(onSelectedComponentChange);
        publishComponent.current = onSelectedComponentChange;

        // E NON SI PUBBLICA IL VUOTO SE NON SI AVEVA NIENTE DA DIRE. La colonna di destra è UNA
        // per tutte le sezioni, ma gli alberi che ci scrivono dentro sono tanti — e non tutti sono
        // in vista: la libreria dei materiali resta montata mentre guardi gli oggetti, perché
        // trasloca invece di rinascere. Un albero fermo, senza niente di aperto, pubblicava `null`
        // a ogni ridisegno: cancellava il pannello che un'ALTRA sezione aveva appena aperto, e da
        // fuori era «clicco una riga e non succede niente».
        //
        // Qui il vuoto lo pubblica solo chi prima aveva pubblicato qualcosa — cioè chi sta davvero
        // chiudendo il proprio pannello. Chi non ha mai scritto in quella colonna non ha titolo per
        // cancellarla.
        const publishedNodeId = useRef<string | null>(null);

        useEffect(() => {
            if (!disableOverlay) return;

            const openNode = overlayNodeId ? findNodeById(overlayNodeId) : undefined;

            if (!openNode?.component) {
                // SI TIENE IL VECCHIO FINCHÉ NON ARRIVA IL NUOVO. Se qualcuno dichiara ancora di avere
                // una riga aperta ma quella riga non si trova, non è una chiusura: è una sostituzione a
                // metà strada — la luce di prima è stata rifatta e la sua erede sta per comparire.
                // Svuotare la colonna in quel varco la fa richiudere e riaprire, e la scena 3D accanto
                // si allarga e si restringe due volte per un gesto solo.
                //
                // Il vuoto lo pubblica solo chi non ha più NIENTE di aperto, cioè chi ha chiuso davvero.
                if (overlayNodeId) return;

                if (publishedNodeId.current !== null) {
                    publishedNodeId.current = null;
                    publishComponent.current?.(null);
                }
                return;
            }

            publishedNodeId.current = overlayNodeId;

            publishComponent.current?.(typeof openNode.component === 'function' ? openNode.component() : openNode.component);
            // `findNodeById` cambia identità esattamente quando cambia l'albero: è lui a dire
            // «qualcosa si è mosso, il pannello va rifatto».
        }, [overlayNodeId, findNodeById, disableOverlay]);

        // «DOVE STA?» — la riga si accende e si spegne tre volte, e prima di tutto si porta in vista.
        //
        // Non cambia la selezione e non apre niente: è una risposta, non un comando. Chi la chiede è
        // la freccetta nella testata dell'Inspector — sei già sul soggetto, e vuoi sapere dove sta
        // nella colonna — ma la usa anche chi arriva da un altro elenco, dopo aver selezionato.
        const [blinkingId, setBlinkingId] = useState<string | null>(null);
        const blinkTimer = useRef<number | null>(null);

        // LA RIGA IN SCRITTURA È UNA SOLA, e il suo abbandono può essere una conferma o una rinuncia.
        // Il tasto di fuga non può limitarsi a chiudere: chiudendo, il campo perde il fuoco e l'uscita
        // scriverebbe comunque quello che c'era dentro. Il flag dice quale delle due è stata.
        const [renamingId, setRenamingId] = useState<string | null>(null);
        const [renameDraft, setRenameDraft] = useState('');
        const renameCancelled = useRef(false);

        useEffect(() => {
            if (!blinkRequest) return;

            const nodeId = blinkRequest.nodeId;
            setBlinkingId(nodeId);

            // La riga può essere comparsa proprio adesso — un ramo appena aperto — quindi si cerca
            // dopo che il browser ha disegnato.
            requestAnimationFrame(() => {
                const target = typeof CSS !== 'undefined' && CSS.escape ? CSS.escape(String(nodeId)) : nodeId;
                treeContainerRef.current?.querySelector(`[data-node-id="${target}"]`)?.scrollIntoView({ block: 'nearest' });
            });

            blinkTimer.current = window.setTimeout(() => setBlinkingId(null), BLINK_DURATION);

            return () => {
                if (blinkTimer.current) window.clearTimeout(blinkTimer.current);
            };
        }, [blinkRequest]);

        // Esponi i metodi tramite useImperativeHandle
        useImperativeHandle(
            ref,
            () => ({
                closeOverlay: () => {
                    setOverlayNodeId(null);
                    onSelectedComponentChange?.(null);
                },
                // APRIRE IL NOME DI UNA RIGA DA FUORI: serve a chi crea qualcosa e vuole che il nome
                // sia già in scrittura, senza far passare da una finestra.
                startRename: (nodeId: string) => {
                    const target = findNodeById(nodeId);
                    if (!target) return;

                    renameCancelled.current = false;
                    setRenameDraft(target.name);
                    setRenamingId(nodeId);
                },
                selectAndOpenOverlay: (dataID: string) => {
                    // 1) Usa la ricerca ricorsiva su tutti i rami dell'albero
                    const targetNode = findNodeByDataID(treeState.nodes, dataID);

                    if (!targetNode) {
                        console.warn(`Nessun nodo trovato per meshId ${dataID}`);
                        return;
                    }

                    if (!targetNode.doNotOpenOverlay && targetNode.component) {
                        onSelectedComponentChange?.(typeof targetNode.component === 'function' ? targetNode.component() : targetNode.component);
                    }

                    // 2) Trova tutti i nodi padre che devono essere aperti
                    const nodesToOpen: string[] = [];
                    let currentId = targetNode.id;

                    while (currentId) {
                        const parentId = findParentId(currentId);
                        if (parentId) {
                            // Aggiungi solo se non è già aperto
                            if (!openNodes.has(parentId)) {
                                nodesToOpen.push(parentId);
                            } else {
                            }
                            currentId = parentId;
                        } else {
                            break;
                        }
                    }

                    // 3) Apri solo i nodi padre che NON sono già aperti
                    nodesToOpen.forEach((nodeId) => {
                        toggleNode(nodeId);
                    });

                    // 4) Selezione
                    setSelectedIds(new Set([targetNode.id]));

                    // 5) Apertura overlay
                    if (targetNode.component && !targetNode.doNotOpenOverlay) {
                        setOverlayNodeId(targetNode.id);
                    }
                }
            }),
            // aggiungo queste dipendenze perchè ad esempio, usando openNodes nella selectAndOpenOverlay, prendendo il valore alla creazione dell'imperative,
            // openNodes restava sempre vuoto, quindi ho aggiunto ognuno per:
            // openNodes - per controllare quali nodi sono aperti
            // findNodeByDataID - per trovare il nodo target
            // findParentId - per risalire la catena dei parent
            // toggleNode - per aprire i nodi
            // setSelectedIds - per selezionare il nodo
            // setOverlayNodeId - per aprire l'overlay
            // treeState.nodes - per la ricerca nel tree
            [setOverlayNodeId, openNodes, findNodeByDataID, findParentId, toggleNode, setSelectedIds, treeState.nodes, onSelectedComponentChange]
        );

        // Gestori Drag and Drop corretti
        const resetDragState = useCallback(() => {
            setDraggedId(null);
            setDropTargetId(null);
            setDragPreview(null);
        }, [setDraggedId, setDropTargetId]);

        // 1) drag start
        const handleDragStart = useCallback(
            (e: React.DragEvent, id: string) => {
                e.stopPropagation();
                e.dataTransfer.effectAllowed = 'move';
                e.dataTransfer.setData('text/plain', id);

                // Se il nodo non è selezionato, selezionalo
                if (!selectedIds.has(id)) {
                    setSelectedIds(new Set([id]));
                }

                setDraggedId(id);

                // Usa il primo nodo selezionato per il preview
                const draggedNodes = selectedIds.has(id) ? Array.from(selectedIds) : [id];
                const first = draggedNodes[0];
                const n = findNodeById(first);

                if (n) {
                    const label = draggedNodes.length > 1 ? `${n.name} (+${draggedNodes.length - 1} others)` : n.name;
                    setDragPreview({
                        x: e.clientX + 8,
                        y: e.clientY + 8,
                        label
                    });
                }
            },
            [selectedIds, findNodeById, setSelectedIds]
        );

        // 2) drag over
        const handleDragOver = useCallback(
            (e: React.DragEvent, id: string) => {
                e.preventDefault();
                e.stopPropagation();

                // Non permettere il drop su un nodo che è tra quelli draggati
                const draggedNodes = draggedId && selectedIds.has(draggedId) ? Array.from(selectedIds) : draggedId ? [draggedId] : [];

                if (draggedNodes.includes(id)) {
                    e.dataTransfer.dropEffect = 'none';
                    return;
                }

                // Verifica che non stiamo droppando un nodo su un suo discendente
                if (draggedId && treeState.findParentId) {
                    let currentParent = treeState.findParentId(id);
                    while (currentParent) {
                        if (draggedNodes.includes(currentParent)) {
                            e.dataTransfer.dropEffect = 'none';
                            return;
                        }
                        currentParent = treeState.findParentId(currentParent);
                    }
                }

                e.dataTransfer.dropEffect = 'move';
                setDropTargetId(id);
            },
            [draggedId, selectedIds, treeState]
        );

        // 3) drop su un nodo
        const handleDrop = useCallback(
            (e: React.DragEvent, targetId: string) => {
                e.preventDefault();
                e.stopPropagation();

                if (!draggedId || !treeState.moveSelection) {
                    resetDragState();
                    return;
                }

                // Determina quali nodi spostare
                const nodesToMove = selectedIds.has(draggedId) ? Array.from(selectedIds) : [draggedId];

                // Non permettere il drop su se stesso o sui propri discendenti
                if (nodesToMove.includes(targetId)) {
                    resetDragState();
                    return;
                }

                // Verifica discendenti
                let isInvalidDrop = false;
                if (treeState.findParentId) {
                    let currentParent = treeState.findParentId(targetId);
                    while (currentParent && !isInvalidDrop) {
                        if (nodesToMove.includes(currentParent)) {
                            isInvalidDrop = true;
                        }
                        currentParent = treeState.findParentId(currentParent);
                    }
                }

                if (isInvalidDrop) {
                    resetDragState();
                    return;
                }

                // In una lista piatta non c'è dentro dove entrare: il trascinamento dice solo dove
                // va messa la riga, e chi ospita l'albero scrive il nuovo ordine.
                if (flatList) {
                    onNodeMoved(nodesToMove[0], targetId, 0);
                    resetDragState();
                    return;
                }

                // 🔧 NUOVO: Controlla se il nodo target può diventare cartella
                const targetNode = treeState.findNodeById(targetId);
                if (targetNode && treeState.canTurnIntoFolder && !treeState.canTurnIntoFolder(targetNode)) {
                    console.warn('Cannot drop on node that cannot turn into folder:', targetNode.name);
                    resetDragState();
                    return;
                }

                // Esegui effettivamente lo spostamento nel TreeNodeManager
                treeState.moveSelection(nodesToMove, targetId);

                // Chiamiamo il callback onNodeMoved per notificare il componente parent
                const targetChildren = targetNode?.children ?? [];
                const insertionIndex = targetChildren.length;
                onNodeMoved(nodesToMove[0], targetId, insertionIndex);

                // Assicurati che il target sia espanso se ha ricevuto dei figli
                if (!openNodes.has(targetId)) {
                    toggleNode(targetId);
                }

                resetDragState();
            },
            [draggedId, selectedIds, treeState, openNodes, toggleNode, resetDragState, onNodeMoved, flatList]
        );

        // TORNARE ALLA RADICE È UNO SPOSTAMENTO COME GLI ALTRI, e per un po' non lo è stato:
        // l'albero spostava il nodo in cima da solo e non avvisava nessuno. A schermo la riga usciva
        // dal suo ramo, ma nella scena la mesh restava figlia di prima — e al primo ridisegno tornava
        // dov'era, come se il gesto non fosse mai avvenuto. `onNodeMoved` con un genitore `null` è il
        // modo in cui questo albero dice «adesso è di nessuno», e chi lo ospita sa cosa farne.
        const dropOnRoot = useCallback(() => {
            if (!draggedId || !treeState.moveSelection) return;

            const nodesToMove = selectedIds.has(draggedId) ? Array.from(selectedIds) : [draggedId];
            treeState.moveSelection(nodesToMove, null);
            onNodeMoved(nodesToMove[0], null, 0);
        }, [draggedId, selectedIds, treeState, onNodeMoved]);

        // 4) dragEnd → fuori dal container
        const handleDragEnd = useCallback(
            (e: React.DragEvent) => {
                // Fuori dall'elenco, in una lista piatta, non c'è una radice in cui tornare: si è
                // già tutti lì.
                if (!draggedId || !treeState.moveSelection || flatList) {
                    resetDragState();
                    return;
                }

                const rect = treeContainerRef.current?.getBoundingClientRect();
                if (rect) {
                    const { clientX: x, clientY: y } = e;
                    const outside = x < rect.left || x > rect.right || y < rect.top || y > rect.bottom;

                    if (outside) dropOnRoot();
                }

                resetDragState();
            },
            [draggedId, treeState, resetDragState, flatList, dropOnRoot]
        );

        // E LO SPAZIO VUOTO DELL'ELENCO È LA RADICE. Lasciare una riga sotto l'ultima è il gesto con
        // cui si tira fuori una mesh da tutto — più naturale che portarla fuori dal pannello — e
        // prima non faceva niente: il contenitore non era un bersaglio, quindi il browser rifiutava
        // il rilascio. Le righe fermano il proprio drop, quindi qui arriva solo ciò che è caduto nel
        // vuoto.
        const handleRootDragOver = useCallback(
            (e: React.DragEvent) => {
                if (!draggedId || flatList) return;
                e.preventDefault();
                e.dataTransfer.dropEffect = 'move';
            },
            [draggedId, flatList]
        );

        const handleRootDrop = useCallback(
            (e: React.DragEvent) => {
                if (!draggedId || flatList) return;
                e.preventDefault();
                dropOnRoot();
                resetDragState();
            },
            [draggedId, flatList, dropOnRoot, resetDragState]
        );

        // 5) drag leave - per resettare il drop target quando usciamo da un nodo
        const handleDragLeave = useCallback(
            (e: React.DragEvent, id: string) => {
                e.preventDefault();
                e.stopPropagation();

                // Resetta il drop target solo se stiamo realmente uscendo dal nodo
                // e non entrando in un suo figlio
                const rect = e.currentTarget.getBoundingClientRect();
                const { clientX: x, clientY: y } = e;
                const outside = x < rect.left || x > rect.right || y < rect.top || y > rect.bottom;

                if (outside && dropTargetId === id) {
                    setDropTargetId(null);
                }
            },
            [dropTargetId]
        );

        // Effetto per aggiornare la posizione del drag preview
        useEffect(() => {
            const onDragOverWindow = (e: DragEvent) => {
                if (dragPreview) {
                    setDragPreview({
                        ...dragPreview,
                        x: e.clientX + 12,
                        y: e.clientY + 12
                    });
                }
            };

            window.addEventListener('dragover', onDragOverWindow);
            return () => window.removeEventListener('dragover', onDragOverWindow);
        }, [dragPreview]);

        // Cleanup quando il componente viene smontato
        useEffect(() => {
            return () => {
                resetDragState();
            };
        }, [resetDragState]);

        // Funzione di rendering ricorsivo
        const renderTree = useCallback(
            (node: any, depth = 0, parent: string | null = null) => {
                const isFolder = node.children?.length > 0;
                const isOpen = expandAll || openNodes.has(node.id);
                const isVis = node.visible !== false;
                const isSel = selectedIds.has(node.id);

                return (
                    <div key={node.id}>
                        <TreeNodeContainer
                            data-node-id={node.id}
                            draggable={!disableDragAndDrop && renamingId !== node.id}
                            onDragStart={(e) => handleDragStart(e, node.id)}
                            onDragOver={(e) => handleDragOver(e, node.id)}
                            onDragLeave={(e) => handleDragLeave(e, node.id)}
                            onDrop={(e) => handleDrop(e, node.id)}
                            onDragEnd={handleDragEnd}
                            $depth={depth}
                            $isDragging={draggedId === node.id}
                            $isDragover={dropTargetId === node.id}
                            $selected={isSel}
                            $isVisible={isVis}
                            $blinking={blinkingId === node.id}
                            role='treeitem'
                            aria-selected={isSel}
                            aria-level={depth + 1}
                            aria-expanded={isFolder ? isOpen : undefined}
                            tabIndex={isSel ? 0 : -1}
                            onKeyDown={(e) => {
                                if (e.key === 'Enter' || e.key === ' ') {
                                    e.preventDefault();
                                    toggleSelection(node.id, false, false);
                                }
                                if (isFolder && e.key === 'ArrowRight' && !isOpen) {
                                    e.preventDefault();
                                    toggleNode(node.id);
                                }
                                if (isFolder && e.key === 'ArrowLeft' && isOpen) {
                                    e.preventDefault();
                                    toggleNode(node.id);
                                }
                            }}
                            onClick={(e) => {
                                node.onNodeClick?.();
                                e.stopPropagation();
                                toggleSelection(node.id, e.shiftKey, e.ctrlKey || e.metaKey);

                                if (node.doNotOpenOverlay) {
                                    setOverlayNodeId(null);
                                    onSelectedComponentChange?.(null);
                                    return;
                                }

                                if (node.component) {
                                    setOverlayNodeId(node.id);
                                    onSelectedComponentChange?.(typeof node.component === 'function' ? node.component() : node.component);
                                }
                            }}
                        >
                            {isFolder && (
                                <TreeTwisty
                                    type='button'
                                    $isOpen={isOpen}
                                    aria-label={`${isOpen ? 'Collapse' : 'Expand'} ${node.name}`}
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        toggleNode(node.id);
                                    }}
                                />
                            )}

                            {node.customIcon ? (
                                <CustomIconContainer>{node.customIcon}</CustomIconContainer>
                            ) : (
                                /* Il glifo dice CHE COSA è il nodo. Se chi costruisce l'albero
                                   lo sa — l'Outliner distingue le mesh vere dai nodi di
                                   trasformazione — lo dichiara in `kind`; le altre liste non
                                   hanno quella distinzione e ricadono sulla forma dell'albero. */
                                <Icon $kind={node.kind}>{(node.kind ?? (isFolder ? 'group' : 'mesh')) === 'group' ? ICONS.folderClosed : ICONS.file}</Icon>
                            )}

                            {renamingId === node.id ? (
                                <NodeNameInput
                                    autoFocus
                                    value={renameDraft}
                                    aria-label={T._('Name', 'Admin')}
                                    draggable={false}
                                    onFocus={(event) => event.currentTarget.select()}
                                    onChange={(event) => setRenameDraft(event.currentTarget.value)}
                                    onClick={(event) => event.stopPropagation()}
                                    onMouseDown={(event) => event.stopPropagation()}
                                    onKeyDown={(event) => {
                                        event.stopPropagation();
                                        if (event.key === 'Enter') event.currentTarget.blur();
                                        if (event.key === 'Escape') {
                                            renameCancelled.current = true;
                                            event.currentTarget.blur();
                                        }
                                    }}
                                    onBlur={(event) => {
                                        const value = event.currentTarget.value.trim();
                                        const cancelled = renameCancelled.current;

                                        renameCancelled.current = false;
                                        setRenamingId(null);

                                        if (!cancelled && value && value !== node.name) onNodeRename?.(node, value);
                                    }}
                                />
                            ) : (
                                <NodeName
                                    $branch={isFolder}
                                    onClick={(event) => {
                                        // IL SECONDO CLIC, NON IL DOPPIO: si apre solo il nome di una riga già scelta,
                                        // e solo se è scelta da sola — con un mucchio selezionato il clic serve ancora a
                                        // ridurre la scelta a questa riga.
                                        if (!onNodeRename || !isSel || selectedIds.size !== 1) return;

                                        event.stopPropagation();
                                        renameCancelled.current = false;
                                        setRenameDraft(node.name);
                                        setRenamingId(node.id);
                                    }}
                                >
                                    {node.name}
                                </NodeName>
                            )}

                            {(() => {
                                const note = rowNote?.(node);
                                if (!note?.text) return null;

                                return (
                                    <RowNote $word={note.word} title={note.title}>
                                        {note.text}
                                    </RowNote>
                                );
                            })()}

                            {(showVisibilityIcon || rowActions) && (
                                <RowTrailing $pinned={!isVis || isSel}>
                                    {showVisibilityIcon && (
                                        <VisibilityIcon
                                            role='button'
                                            aria-label={`${isVis ? 'Hide' : 'Show'} ${node.name}`}
                                            tabIndex={0}
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                // Si passa lo stato VOLUTO, non quello attuale: chi
                                                // ascolta deve applicarlo, non ricalcolarlo.
                                                const nextVisible = !isVis;
                                                toggleVisibility(node.id);
                                                onToggleNodeVisibility?.(node.id, nextVisible);
                                            }}
                                        >
                                            {isVis ? ICONS.eye : ICONS.eyeOff}
                                        </VisibilityIcon>
                                    )}

                                    {/* Il menu riceve i comandi GIÀ legati a questa riga: aprirlo non
                                        cambia la selezione, quindi il soggetto non può che essere lei. */}
                                    {rowActions && <CommandMenu actions={rowActions(node)} />}
                                </RowTrailing>
                            )}
                        </TreeNodeContainer>

                        {isFolder && isOpen && isVis && node.children && (
                            <ChildrenContainer>
                                {[...node.children]
                                    .sort((a: any, b: any) => (a.displayOrder || 0) - (b.displayOrder || 0))
                                    .map((child: any) => renderTree(child, depth + 1, node.id))}
                            </ChildrenContainer>
                        )}
                    </div>
                );
            },
            [
                openNodes,
                selectedIds,
                draggedId,
                dropTargetId,
                disableDragAndDrop,
                toggleSelection,
                toggleNode,
                toggleVisibility,
                handleDragStart,
                handleDragOver,
                handleDrop,
                handleDragEnd,
                setOverlayNodeId,
                rowActions,
                renamingId,
                renameDraft,
                onNodeRename,
                expandAll
            ]
        );

        return (
            <TreeNodeInnerContainer ref={treeContainerRef} role='tree' aria-label={treeLabel} onDragOver={handleRootDragOver} onDrop={handleRootDrop}>
                {/* La COPIA prima di ordinare non è pignoleria: `sort` riordina sul posto, e questo
                    array arriva dal `useMemo` di chi ci ospita — riordinarlo qui vuol dire cambiare
                    uno stato memoizzato durante il render di un altro. */}
                {[...nodes].sort((a: any, b: any) => (a.displayOrder || 0) - (b.displayOrder || 0)).map((node: any) => renderTree(node, 0, null))}

                {dragPreview && (
                    <DragPreviewLabel x={dragPreview.x} y={dragPreview.y}>
                        {dragPreview.label}
                    </DragPreviewLabel>
                )}

                {overlayNodeId && !disableOverlay && (
                    <OverlayPanel
                        onClose={() => {
                            setOverlayNodeId(null);
                            onSelectedComponentChange?.(null);
                        }}
                    >
                        {(() => {
                            const node = findNodeById(overlayNodeId);
                            if (!node?.component) return <div>No component selected.</div>;

                            return typeof node.component === 'function' ? node.component() : node.component;
                        })()}
                    </OverlayPanel>
                )}
            </TreeNodeInnerContainer>
        );
    }
);

export default TreeComponent;
